// Customer Inquiries — pharmacy portal view for driver-filed
// inquiries. Lives under Follow-ups as a sibling tab to the existing
// order-exception buckets (failed_delivery / missing_pod /
// cold_chain / aged_pending).
//
// Data flow:
//   1. RLS-scoped SELECT on public.customer_inquiries (metadata only —
//      encrypted PHI columns stay ciphertext at this stage).
//   2. POST /api/inquiries/reveal-batch — server-side decrypt of
//      notes / callback_phone / resolution_notes for the visible page.
//   3. Resolve action fires POST /api/inquiries/[id]/resolve; the row
//      re-fetches on success so the list reflects the new state.
//
// PHI posture: notes and callback_phone are libsodium ciphertext at
// rest; only the reveal-batch route decrypts them (server-side, key
// stays there). Resolution notes follow the same pattern.

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { LynrxSupabaseClient } from '@lynrx/shared';
import { useAuth } from '../../lib/auth/auth-context';
import { supabase as defaultClient } from '../../lib/supabase';
import { captureSupabaseError } from '../../lib/sentry-supabase';
import { Alert } from '../ui/Alert';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';

// Enum literals mirror the customer_inquiry_category / _status enums
// in migration 20260819010000_customer_inquiries.sql. Kept in sync
// manually — the enums are small and rarely change.
type Category =
  | 'refill_question'
  | 'pharmacist_callback'
  | 'delivery_schedule'
  | 'address_change'
  | 'medication_question'
  | 'billing_question'
  | 'other';
type Status = 'open' | 'acknowledged' | 'resolved';

const CATEGORY_LABELS: Record<Category, string> = {
  refill_question: 'Refill question',
  pharmacist_callback: 'Pharmacist callback',
  delivery_schedule: 'Delivery schedule',
  address_change: 'Address change',
  medication_question: 'Medication question',
  billing_question: 'Billing question',
  other: 'Other',
};

type InquiryRow = {
  id: string;
  pharmacy_id: string;
  patient_id: string | null;
  order_id: string | null;
  driver_user_id: string | null;
  category: Category;
  status: Status;
  created_at: string;
  resolved_at: string | null;
};

type PlainInquiry = {
  notes: string | null;
  callback_phone: string | null;
  resolution_notes: string | null;
};

// Metadata select. Encrypted columns intentionally absent — they
// come from the reveal-batch route.
const INQUIRY_LIST_COLUMNS =
  'id, pharmacy_id, patient_id, order_id, driver_user_id, category, status, created_at, resolved_at';

const LIST_LIMIT = 100;

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

type ViewProps = {
  client?: LynrxSupabaseClient;
};

export function CustomerInquiriesView({
  client = defaultClient,
}: ViewProps): JSX.Element {
  const { session, isReady } = useAuth();
  const [rows, setRows] = useState<InquiryRow[] | null>(null);
  const [plain, setPlain] = useState<Record<string, PlainInquiry>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Filter: default to non-resolved so the list surfaces work items.
  // "all" reveals resolved inquiries too — useful for looking up a
  // past resolution notes.
  const [filter, setFilter] = useState<'open' | 'all'>('open');
  const [resolveFor, setResolveFor] = useState<InquiryRow | null>(null);

  const fetchAll = useCallback(async () => {
    if (!session) return;
    setLoading(true);
    setError(null);
    // We don't yet have canonical Database['Tables'] types for
    // customer_inquiries (regen after migration lands). Cast at the
    // client boundary — the shape is validated by INQUIRY_LIST_COLUMNS.
    const query = client
      .from('customer_inquiries' as never)
      .select(INQUIRY_LIST_COLUMNS)
      .order('created_at', { ascending: false })
      .limit(LIST_LIMIT);
    const { data, error: err } = (await query) as unknown as {
      data: InquiryRow[] | null;
      error: unknown;
    };
    if (err) {
      captureSupabaseError(err, 'inquiries.list.fetch');
      setError('Could not load customer inquiries.');
      setLoading(false);
      return;
    }
    const listRows = data ?? [];
    // Hydrate encrypted fields BEFORE committing rows — matches the
    // /patients pattern (2026-08-19 rework): show a loading strip
    // until both queries resolve so the row and its notes render
    // in one paint.
    const missing = listRows.map((r) => r.id).filter((id) => !(id in plain));
    if (missing.length > 0) {
      try {
        const res = await fetch('/api/inquiries/reveal-batch', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ inquiry_ids: missing }),
        });
        if (res.ok) {
          const body = (await res.json().catch(() => null)) as
            | { inquiries: Record<string, PlainInquiry> }
            | null;
          if (body?.inquiries) {
            setPlain((prev) => ({ ...prev, ...body.inquiries }));
          }
        }
      } catch {
        // Silent — cells fall back to a placeholder rather than
        // blocking the whole list.
      }
    }
    setRows(listRows);
    setLoading(false);
  }, [client, session, plain]);

  useEffect(() => {
    if (!isReady || !session) return;
    void fetchAll();
    // fetchAll depends on `plain` — re-firing on plain changes is
    // fine because the missing filter short-circuits when everything
    // is already resolved.
  }, [isReady, session, fetchAll]);

  const visible = useMemo(() => {
    if (!rows) return null;
    if (filter === 'all') return rows;
    return rows.filter((r) => r.status !== 'resolved');
  }, [rows, filter]);

  const openCount = useMemo(
    () => (rows ?? []).filter((r) => r.status !== 'resolved').length,
    [rows],
  );

  if (rows === null && loading) {
    return (
      <div className="rounded-lg border border-border bg-bg p-6 text-sm text-ink-2">
        Loading customer inquiries…
      </div>
    );
  }

  return (
    <>
      {error ? (
        <div className="mb-4">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

      <div className="mb-3 flex items-center gap-2">
        <FilterChip
          active={filter === 'open'}
          label="Open"
          count={openCount}
          onClick={() => setFilter('open')}
        />
        <FilterChip
          active={filter === 'all'}
          label="All"
          count={rows?.length ?? 0}
          onClick={() => setFilter('all')}
        />
      </div>

      {visible && visible.length === 0 ? (
        <EmptyState filter={filter} />
      ) : (
        <div className="space-y-3">
          {visible?.map((r) => (
            <InquiryCard
              key={r.id}
              row={r}
              plain={plain[r.id]}
              onResolve={() => setResolveFor(r)}
            />
          ))}
        </div>
      )}

      {resolveFor ? (
        <ResolveModal
          row={resolveFor}
          onClose={() => setResolveFor(null)}
          onResolved={() => {
            setResolveFor(null);
            void fetchAll();
          }}
        />
      ) : null}
    </>
  );
}

function FilterChip({
  active,
  label,
  count,
  onClick,
}: {
  active: boolean;
  label: string;
  count: number;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium transition ' +
        (active
          ? 'border-ink bg-ink text-white'
          : 'border-border bg-bg text-ink-2 hover:bg-bg-hover')
      }
    >
      <span>{label}</span>
      <span
        className={
          'rounded-full px-1.5 py-0.5 text-[10px] leading-none ' +
          (active ? 'bg-white/20' : 'bg-border-muted')
        }
      >
        {count}
      </span>
    </button>
  );
}

function EmptyState({ filter }: { filter: 'open' | 'all' }): JSX.Element {
  return (
    <div className="rounded-lg border border-border bg-bg p-8 text-center">
      <p className="text-sm font-medium text-ink">
        {filter === 'open' ? 'No open inquiries.' : 'No customer inquiries yet.'}
      </p>
      <p className="mt-1 text-xs text-ink-3">
        {filter === 'open'
          ? 'When a driver files an inquiry it will appear here.'
          : 'Drivers can file inquiries from the driver app when a patient asks something the pharmacy needs to answer.'}
      </p>
    </div>
  );
}

function InquiryCard({
  row,
  plain,
  onResolve,
}: {
  row: InquiryRow;
  plain: PlainInquiry | undefined;
  onResolve: () => void;
}): JSX.Element {
  const resolved = row.status === 'resolved';
  return (
    <div className="rounded-lg border border-border bg-bg p-4 shadow-card">
      <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-ink-3">
        <span className="rounded-full bg-primary/10 px-2 py-0.5 font-medium text-primary">
          {CATEGORY_LABELS[row.category]}
        </span>
        <span>·</span>
        <span>{formatWhen(row.created_at)}</span>
        {resolved && row.resolved_at ? (
          <>
            <span>·</span>
            <span className="rounded-full bg-green-50 px-2 py-0.5 font-medium text-green-700">
              Resolved {formatWhen(row.resolved_at)}
            </span>
          </>
        ) : (
          <>
            <span>·</span>
            <span className="rounded-full bg-amber-50 px-2 py-0.5 font-medium text-amber-800">
              {row.status === 'acknowledged' ? 'Acknowledged' : 'Open'}
            </span>
          </>
        )}
      </div>

      <div className="mb-3 whitespace-pre-wrap text-sm text-ink">
        {plain === undefined ? (
          <span className="text-ink-3">Loading notes…</span>
        ) : plain.notes ? (
          plain.notes
        ) : (
          <span className="text-ink-3">—</span>
        )}
      </div>

      {plain?.callback_phone ? (
        <div className="mb-3 text-xs text-ink-2">
          Callback:{' '}
          <span className="font-medium text-ink">{plain.callback_phone}</span>
        </div>
      ) : null}

      {resolved && plain?.resolution_notes ? (
        <div className="mb-3 rounded-md bg-bg-muted p-3 text-xs">
          <div className="mb-1 font-medium text-ink-2">Resolution</div>
          <div className="whitespace-pre-wrap text-ink">
            {plain.resolution_notes}
          </div>
        </div>
      ) : null}

      {!resolved ? (
        <div className="mt-2 flex justify-end">
          <Button variant="secondary" onClick={onResolve}>
            Resolve
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function ResolveModal({
  row,
  onClose,
  onResolved,
}: {
  row: InquiryRow;
  onClose: () => void;
  onResolved: () => void;
}): JSX.Element {
  const { session } = useAuth();
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = useCallback(async () => {
    if (!session) return;
    setSubmitting(true);
    setErr(null);
    try {
      const res = await fetch(`/api/inquiries/${row.id}/resolve`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ resolution_notes: notes.trim() || undefined }),
      });
      if (!res.ok) {
        setErr(`Could not resolve (${res.status}).`);
        setSubmitting(false);
        return;
      }
      onResolved();
    } catch {
      setErr('Network error — please try again.');
      setSubmitting(false);
    }
  }, [session, row.id, notes, onResolved]);

  return (
    <Modal open={true} onClose={onClose} title="Resolve inquiry">
      <div className="space-y-3">
        <p className="text-xs text-ink-2">
          Optional notes about what action was taken. Encrypted at rest.
        </p>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={5}
          maxLength={2000}
          placeholder="e.g. Called patient, confirmed next delivery is 8/22."
          className="w-full rounded-md border border-border bg-bg p-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary"
        />
        {err ? <Alert tone="error">{err}</Alert> : null}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void submit()}
            disabled={submitting}
          >
            {submitting ? 'Resolving…' : 'Resolve'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
